// src/components/common/ErrorBoundary.js

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  Collapse,
} from '@mui/material';
import {
  ErrorOutline,
  Refresh,
  BugReport,
  ExpandMore,
  ExpandLess,
} from '../icons';
import { logSystemEvent } from '../../services/systemLogger';
import { colors, fonts } from '../../theme/tokens';
import { vaultWash } from './VaultLayout';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Stale CRA/webpack chunks after HMR or redeploy — reload once, then show UI.
    const isChunkLoad =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [\w-]+ failed/i.test(error?.message || '') ||
      /Failed to fetch dynamically imported module/i.test(error?.message || '');
    if (isChunkLoad && typeof window !== 'undefined') {
      const key = 'fd_chunk_reload';
      const last = Number(sessionStorage.getItem(key) || 0);
      const now = Date.now();
      // Reload once per incident; ignore flag older than 10s (successful recovery).
      if (!last || now - last > 10000) {
        sessionStorage.setItem(key, String(now));
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(key);
    }

    // Generate unique error ID
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Log error to system
    this.logError(error, errorInfo, errorId);

    // Log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  logError = async (error, errorInfo, errorId) => {
    try {
      await logSystemEvent({
        level: 'error',
        category: 'react',
        message: `React Error Boundary: ${error.message}`,
        metadata: {
          errorId,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          props: this.props,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        },
        source: this.props.componentName || 'ErrorBoundary',
      });
    } catch (logError) {
      console.error('Failed to log error:', logError);
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails, errorId } = this.state;
      const { fallback: FallbackComponent, minimal = false } = this.props;

      // If a custom fallback component is provided, use it
      if (FallbackComponent) {
        return (
          <FallbackComponent
            error={error}
            errorInfo={errorInfo}
            errorId={errorId}
            onRetry={this.handleRetry}
            onReload={this.handleReload}
          />
        );
      }

      // Minimal error display for smaller components
      if (minimal) {
        return (
          <Alert 
            severity="error" 
            action={
              <Button size="small" onClick={this.handleRetry}>
                Retry
              </Button>
            }
          >
            Something went wrong. Please try again.
          </Alert>
        );
      }

      // Full error boundary display
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              maxWidth: 800,
              width: '100%',
              borderRadius: '2px',
              background: vaultWash,
              border: `1px solid ${colors.border}`,
              boxShadow: `inset 0 0 0 1px ${colors.brassDark}33`,
            }}
          >
            <ErrorOutline
              sx={{
                fontSize: 64,
                color: colors.brass,
                mb: 2,
              }}
            />
            
            <Typography
              variant="h4"
              sx={{
                color: colors.textPrimary,
                fontFamily: fonts.display,
                fontWeight: 400,
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Something went wrong
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: colors.textSecondary,
                fontFamily: fonts.body,
                mb: 3,
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              This screen hit an unexpected error. Try again, or reload to get back into the vault.
            </Typography>

            {errorId && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontFamily: 'monospace',
                  mb: 3,
                  display: 'block',
                }}
              >
                Error ID: {errorId}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleRetry}
                sx={{
                  background: colors.brass,
                  color: colors.void,
                  fontFamily: fonts.display,
                  fontWeight: 400,
                  '&:hover': {
                    background: colors.brassBright,
                  },
                }}
              >
                Try Again
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                sx={{
                  borderColor: colors.border,
                  color: colors.textPrimary,
                  fontFamily: fonts.display,
                  fontWeight: 400,
                  '&:hover': {
                    backgroundColor: colors.panelRaised,
                    borderColor: colors.brass,
                    color: colors.brass,
                  },
                }}
              >
                Reload Page
              </Button>
            </Box>

            {/* Technical Details (Development/Debug) */}
            {(process.env.NODE_ENV === 'development' || this.props.showTechnicalDetails) && (
              <>
                <Button
                  startIcon={<BugReport />}
                  endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                  onClick={this.toggleDetails}
                  sx={{ mb: 2 }}
                >
                  {showDetails ? 'Hide' : 'Show'} Technical Details
                </Button>

                <Collapse in={showDetails}>
                  <Alert severity="warning" sx={{ textAlign: 'left', mt: 2 }}>
                    <AlertTitle>Technical Details</AlertTitle>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 300,
                        overflow: 'auto',
                        mt: 1,
                      }}
                    >
                      {error && error.toString()}
                      {errorInfo && errorInfo.componentStack}
                    </Typography>
                  </Alert>
                </Collapse>
              </>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
